import axios from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import {
	TShopifyCreateSalesChannelRequest,
	TShopifyDeleteSalesChannelRequest,
	TShopifyDeleteSalesChannelResponse,
	TShopifySalesChannel,
	TShopifySalesChannelsCatalogRequest,
	TShopifySalesChannelsCatalogResponse,
	TShopifyStoreSalesChannelsRequest,
	TShopifyStoreSalesChannelsResponse,
	TShopifyStoreSettingsRequest,
	TShopifyStoreSettingsResponse,
	TShopifyStoreSyncRequest,
	TShopifyStoreSyncResponse,
	TShopifyStoreUpdateSalesChannelsRequest,
	TShopifyStoreUpdateSettingsRequest,
	TShopifyStoreUpdateSettingsResponse,
	TShopifyStoresByClientIdRequest,
	TShopifyStoresByClientIdResponse,
	TShopifyUpdateSalesChannelRequest,
} from './types';
import {
	getStoresByClientIdRequest,
	getStoresByClientIdSuccess,
	getStoresByClientIdFailure,
	getStoreSettingsRequest,
	getStoreSettingsSuccess,
	getStoreSettingsFailure,
	updateStoreSettingsRequest,
	updateStoreSettingsSuccess,
	updateStoreSettingsFailure,
	getStoreSalesChannelsRequest,
	getStoreSalesChannelsSuccess,
	getStoreSalesChannelsFailure,
	updateStoreSalesChannelsRequest,
	updateStoreSalesChannelsSuccess,
	updateStoreSalesChannelsFailure,
	syncStoreRequest,
	syncStoreSuccess,
	syncStoreFailure,
	listSalesChannelsCatalogRequest,
	listSalesChannelsCatalogSuccess,
	listSalesChannelsCatalogFailure,
	getSalesChannelRequest,
	getSalesChannelSuccess,
	getSalesChannelFailure,
	createSalesChannelRequest,
	createSalesChannelSuccess,
	createSalesChannelFailure,
	updateSalesChannelRequest,
	updateSalesChannelSuccess,
	updateSalesChannelFailure,
	deleteSalesChannelRequest,
	deleteSalesChannelSuccess,
	deleteSalesChannelFailure,
} from './reducer';

function* getStoresByClientIdWorker(action: PayloadAction<TShopifyStoresByClientIdRequest>) {
	try {
		const response: TShopifyStoresByClientIdResponse = yield call(
			api.shopifyStores.getStoresByClientId,
			action.payload,
		);
		yield put(getStoresByClientIdSuccess(response));
	} catch (error) {
		yield put(getStoresByClientIdFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getStoreSettingsWorker(action: PayloadAction<TShopifyStoreSettingsRequest>) {
	try {
		const response: TShopifyStoreSettingsResponse = yield call(
			api.shopifyStores.getStoreSettings,
			action.payload,
		);
		yield put(getStoreSettingsSuccess(response));
	} catch (error) {
		yield put(getStoreSettingsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateStoreSettingsWorker(action: PayloadAction<TShopifyStoreUpdateSettingsRequest>) {
	try {
		const response: TShopifyStoreUpdateSettingsResponse = yield call(
			api.shopifyStores.updateStoreSettings,
			action.payload,
		);
		yield put(updateStoreSettingsSuccess(response));
		notificationContainer('Store settings updated successfully.', 'success');
	} catch (error) {
		yield put(updateStoreSettingsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getStoreSalesChannelsWorker(action: PayloadAction<TShopifyStoreSalesChannelsRequest>) {
	try {
		const response: TShopifyStoreSalesChannelsResponse = yield call(
			api.shopifyStores.getStoreSalesChannels,
			action.payload,
		);
		yield put(getStoreSalesChannelsSuccess(response));
	} catch (error) {
		yield put(getStoreSalesChannelsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateStoreSalesChannelsWorker(
	action: PayloadAction<TShopifyStoreUpdateSalesChannelsRequest>,
) {
	try {
		const response: TShopifyStoreSalesChannelsResponse = yield call(
			api.shopifyStores.updateStoreSalesChannels,
			action.payload,
		);
		yield put(updateStoreSalesChannelsSuccess(response));
		notificationContainer('Sales channels updated successfully.', 'success');
	} catch (error) {
		yield put(updateStoreSalesChannelsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* syncStoreWorker(action: PayloadAction<TShopifyStoreSyncRequest>) {
	try {
		const response: TShopifyStoreSyncResponse = yield call(
			api.shopifyStores.syncStore,
			action.payload,
		);
		yield put(syncStoreSuccess(response));
		notificationContainer('Store sync started.', 'success');
	} catch (error) {
		yield put(syncStoreFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* listSalesChannelsCatalogWorker(
	action: PayloadAction<TShopifySalesChannelsCatalogRequest>,
) {
	try {
		const response: TShopifySalesChannelsCatalogResponse = yield call(
			api.shopifyStores.listSalesChannelsCatalog,
			action.payload,
		);
		yield put(listSalesChannelsCatalogSuccess(response));
	} catch (error) {
		yield put(listSalesChannelsCatalogFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getSalesChannelWorker(action: PayloadAction<number>) {
	try {
		const response: TShopifySalesChannel = yield call(
			api.shopifyStores.getSalesChannel,
			action.payload,
		);
		yield put(getSalesChannelSuccess(response));
	} catch (error) {
		yield put(getSalesChannelFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* createSalesChannelWorker(action: PayloadAction<TShopifyCreateSalesChannelRequest>) {
	try {
		yield call(api.shopifyStores.createSalesChannel, action.payload);
		yield put(createSalesChannelSuccess());
		notificationContainer('Sales channel created successfully.', 'success');
	} catch (error) {
		yield put(createSalesChannelFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateSalesChannelWorker(action: PayloadAction<TShopifyUpdateSalesChannelRequest>) {
	try {
		yield call(api.shopifyStores.updateSalesChannel, action.payload);
		yield put(updateSalesChannelSuccess());
		notificationContainer('Sales channel updated successfully.', 'success');
	} catch (error) {
		yield put(updateSalesChannelFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* deleteSalesChannelWorker(action: PayloadAction<TShopifyDeleteSalesChannelRequest>) {
	try {
		const response: TShopifyDeleteSalesChannelResponse = yield call(
			api.shopifyStores.deleteSalesChannel,
			action.payload,
		);
		yield put(deleteSalesChannelSuccess());
		notificationContainer(response?.message ?? 'Sales channel deleted.', 'success');
	} catch (error) {
		yield put(deleteSalesChannelFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

export function* shopifyStoresSaga() {
	yield takeLatest(getStoresByClientIdRequest.type, getStoresByClientIdWorker);
	yield takeLatest(getStoreSettingsRequest.type, getStoreSettingsWorker);
	yield takeLatest(updateStoreSettingsRequest.type, updateStoreSettingsWorker);
	yield takeLatest(getStoreSalesChannelsRequest.type, getStoreSalesChannelsWorker);
	yield takeLatest(updateStoreSalesChannelsRequest.type, updateStoreSalesChannelsWorker);
	yield takeLatest(syncStoreRequest.type, syncStoreWorker);
	yield takeLatest(listSalesChannelsCatalogRequest.type, listSalesChannelsCatalogWorker);
	yield takeLatest(getSalesChannelRequest.type, getSalesChannelWorker);
	yield takeLatest(createSalesChannelRequest.type, createSalesChannelWorker);
	yield takeLatest(updateSalesChannelRequest.type, updateSalesChannelWorker);
	yield takeLatest(deleteSalesChannelRequest.type, deleteSalesChannelWorker);
}
